import { Navbar, Footer } from "@/components/site-chrome";
import { Button } from "@/components/ui/button";
import { Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { Building2, Receipt, BarChart3, Bell, ArrowRight, CheckCircle2 } from "lucide-react";
import {
  CreamBackground, DarkBackground, WineBackground,
  SectionLabel, Reveal, DisplayHeadline, SandCorner,
  WINE, WINE_L, CREAM, CREAM2, CHARCOAL, SAND, FONT_DISPLAY, FONT_SANS,
} from "@/components/landing/editorial-backgrounds";

const features = [
  { icon: Receipt,    title: "Zero reconciliation",      desc: "One consolidated payment auto-splits into every service wallet. No manual posting." },
  { icon: BarChart3,  title: "Real-time collections",     desc: "See exactly who has paid, what's outstanding, and which wallets need attention." },
  { icon: Bell,       title: "Automated reminders",      desc: "Parents get SMS and WhatsApp reminders before fees are due. Collection rates go up." },
  { icon: Building2,  title: "Multi-campus ready",       desc: "Manage multiple campuses, streams and year groups from a single console." },
];

const stats = [
  { label: "Collection rate",     value: "94%" },
  { label: "Reconciliation time", value: "0 min" },
  { label: "Mobile Money fees",   value: "1 charge" },
  { label: "Audit trail",        value: "10 years" },
];

const benefits = [
  "Automated fee splitting across tuition, transport, meals, uniforms",
  "Real-time parent payment notifications via SMS & WhatsApp",
  "Daily settlement reports to your bank account",
  "Multi-campus, multi-stream, multi-year-group support",
  "Merchant management for canteen, bookshop, clinic",
  "10-year audit-ready transaction trail with WTX references",
];

export function SchoolsPage() {
  return (
    <CreamBackground>
      <Navbar />

      {/* ── HERO ── */}
      <section className="mx-auto max-w-7xl px-6 pb-20 pt-16 sm:px-8 sm:pt-24">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <div>
            <SectionLabel label="For Schools" />
            <DisplayHeadline top="Turn fee collection" bottom="into one tap." />

            <Reveal delay={0.2}>
              <p className="mt-6 max-w-lg text-base leading-relaxed sm:text-lg" style={{ fontFamily: FONT_DISPLAY, fontStyle: "italic", color: `${CHARCOAL}80` }}>
                BRECCS Pay consolidates every obligation into a single PRN and fans out
                to each service wallet automatically. No more reconciliation. No more
                chasing parents for individual payments.
              </p>
            </Reveal>

            <Reveal delay={0.3}>
              <div className="mt-10 flex flex-wrap gap-3">
                <Button asChild size="lg" className="h-12 rounded-none px-8 text-[13px] font-bold uppercase tracking-[0.15em] text-white hover:opacity-90" style={{ background: WINE, border: "none", fontFamily: FONT_SANS }}>
                  <Link to="/auth">Get Started <ArrowRight className="ml-2 h-4 w-4" /></Link>
                </Button>
                <Button asChild variant="ghost" size="lg" className="h-12 rounded-none px-7 text-[13px] font-bold uppercase tracking-[0.15em] hover:bg-transparent" style={{ border: `1.5px solid ${CHARCOAL}30`, color: CHARCOAL, fontFamily: FONT_SANS }}>
                  <Link to="/auth">Sign In</Link>
                </Button>
              </div>
            </Reveal>
          </div>

          {/* Stats grid — editorial info boxes */}
          <Reveal delay={0.25}>
            <div className="grid grid-cols-2 gap-px" style={{ border: `1.5px solid ${CHARCOAL}20` }}>
              {stats.map((s, i) => (
                <div key={s.label} className="px-6 py-8" style={{ background: i % 2 === 0 ? CREAM : CREAM2, borderRight: i % 2 === 0 ? `1px solid ${CHARCOAL}15` : "none", borderBottom: i < 2 ? `1px solid ${CHARCOAL}15` : "none" }}>
                  <div className="text-[9px] font-bold uppercase tracking-[0.25em]" style={{ color: WINE, fontFamily: FONT_SANS }}>{s.label}</div>
                  <div className="mt-2 text-4xl font-bold leading-none tracking-tight" style={{ color: CHARCOAL, fontFamily: FONT_DISPLAY }}>{s.value}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section className="mx-auto max-w-7xl px-6 py-20 sm:px-8">
        <SectionLabel label="Capabilities" />
        <div className="mt-12 grid gap-px md:grid-cols-2 lg:grid-cols-4" style={{ border: `1.5px solid ${CHARCOAL}15` }}>
          {features.map((f, i) => (
            <Reveal key={f.title} delay={i * 0.08}>
              <div className="h-full p-8" style={{ background: i % 2 === 0 ? CREAM : CREAM2, borderRight: i < 3 ? `1px solid ${CHARCOAL}10` : "none" }}>
                <div className="flex h-12 w-12 items-center justify-center" style={{ border: `1.5px solid ${WINE}30`, background: `${WINE}08` }}>
                  <f.icon className="h-5 w-5" style={{ color: WINE }} />
                </div>
                <h3 className="mt-5 text-lg font-bold" style={{ color: CHARCOAL, fontFamily: FONT_DISPLAY }}>{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: `${CHARCOAL}70`, fontFamily: FONT_SANS }}>{f.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ── BENEFITS — dark section ── */}
      <DarkBackground>
        <section className="mx-auto max-w-5xl px-6 py-24 sm:px-8">
          <div className="flex items-center justify-center gap-4">
            <div className="h-px w-12" style={{ background: "rgba(255,255,255,0.25)" }} />
            <span className="text-[10px] font-bold uppercase tracking-[0.3em]" style={{ color: "rgba(255,255,255,0.55)", fontFamily: FONT_SANS }}>What You Get</span>
            <div className="h-px w-12" style={{ background: "rgba(255,255,255,0.25)" }} />
          </div>
          <Reveal delay={0.1}>
            <h2 className="mt-8 text-center text-white" style={{ fontFamily: FONT_DISPLAY, lineHeight: 0.95, letterSpacing: "-0.03em", fontSize: "clamp(2rem, 5vw, 4rem)", fontWeight: 700 }}>
              Everything included.
            </h2>
            <p className="mt-1 text-center" style={{ fontFamily: FONT_DISPLAY, color: SAND, lineHeight: 0.95, letterSpacing: "-0.03em", fontSize: "clamp(2rem, 5vw, 4rem)", fontWeight: 700, fontStyle: "italic" }}>
              Nothing extra to buy.
            </p>
          </Reveal>
          <div className="mt-14 grid gap-4 sm:grid-cols-2">
            {benefits.map((item, i) => (
              <Reveal key={item} delay={i * 0.06}>
                <div className="flex items-start gap-3 p-4" style={{ border: `1px solid ${SAND}20`, background: `${SAND}08` }}>
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" style={{ color: SAND }} />
                  <span className="text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.85)", fontFamily: FONT_SANS }}>{item}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      </DarkBackground>

      {/* ── CTA ── */}
      <WineBackground>
        <section className="mx-auto max-w-3xl px-6 py-28 text-center sm:py-36">
          <div className="relative p-12 sm:p-16" style={{ border: `1.5px solid ${SAND}40` }}>
            <SandCorner position="tl" />
            <SandCorner position="tr" />
            <SandCorner position="bl" />
            <SandCorner position="br" />
            <Reveal>
              <h2 className="text-white" style={{ fontFamily: FONT_DISPLAY, lineHeight: 0.92, letterSpacing: "-0.03em", fontSize: "clamp(2.5rem, 7vw, 5rem)", fontWeight: 700 }}>
                Run your school
              </h2>
              <p style={{ fontFamily: FONT_DISPLAY, color: SAND, lineHeight: 0.92, letterSpacing: "-0.03em", fontSize: "clamp(2.5rem, 7vw, 5rem)", fontWeight: 700, fontStyle: "italic" }}>
                on one platform.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mx-auto mt-8 max-w-md text-base leading-relaxed" style={{ fontFamily: FONT_DISPLAY, fontStyle: "italic", color: "rgba(255,255,255,0.65)" }}>
                See a 30-minute private demonstration tailored to your institution.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <Button asChild size="lg" className="mt-10 h-12 rounded-none px-8 text-[13px] font-bold uppercase tracking-[0.15em] hover:opacity-90" style={{ background: SAND, color: CHARCOAL, border: "none", fontFamily: FONT_SANS }}>
                <Link to="/auth">Schedule Demonstration <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </Reveal>
          </div>
        </section>
      </WineBackground>

      <Footer />
    </CreamBackground>
  );
}
